/**
 * `<MarkdownImportPanel>` — Markdown folder import card on the Sources tab.
 *
 * v4 P8 refit (2026-05-18, GAP_ANALYSIS_R2.md §5 / SO4): the path input +
 * Browse + Import collapse into one inline row per SO4.
 *
 * U7 refit (2026-07-05): adopted the canonical `<SettingsCard>` chrome (the
 * `.s-card` grammar) so Sources matches the General tab. The "Previous
 * imports" footer was removed — its only content was a leaked dev-TODO
 * ("GET /sources/markdown/imports is not yet mounted…"); restore a history
 * footer when the read endpoint actually exists. Path placeholder is
 * Windows-style (this is a Windows-first app).
 *
 * Wires `POST /sources/markdown/import` via `useMarkdownImportMutation`.
 */

import { useState } from 'react';
import { useMarkdownImportMutation } from '@/hooks';
import { SettingsCard } from '@/surfaces/settings/SettingsCard';

interface MarkdownImportPanelProps {
  active: boolean;
}

export function MarkdownImportPanel(_props: MarkdownImportPanelProps): JSX.Element {
  const [path, setPath] = useState('');
  const importMutation = useMarkdownImportMutation();
  const trimmed = path.trim();
  const canSubmit = trimmed.length > 0 && !importMutation.isPending;

  // Result message handling — the mutation reports accepted/rejected. We
  // surface it inline below the import row; it auto-clears on next change.
  const result = importMutation.data;

  return (
    <SettingsCard
      id="markdown"
      title="Markdown folder import"
      desc="Bulk-import a markdown file or directory of notes; each file becomes one note."
    >
      <div className="source-import-row">
        <input
          className="source-import-row__input"
          value={path}
          placeholder="C:\notes\export"
          onChange={(e) => setPath(e.target.value)}
          aria-label="Path to markdown file or folder"
        />
        <button
          type="button"
          className="btn btn--sm"
          disabled
          title="Native file-picker integration ships in v1.5 — paste a path for now"
        >
          Browse…
        </button>
        <button
          type="button"
          className="btn btn--sm btn--primary"
          disabled={!canSubmit}
          onClick={() => importMutation.mutate({ path: trimmed })}
        >
          {importMutation.isPending ? 'Importing…' : 'Import'}
        </button>
      </div>

      {result !== undefined && (
        <div
          role="status"
          style={{
            marginTop: 8,
            padding: '6px 10px',
            borderRadius: 'var(--r-xs)',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: result.accepted ? 'var(--good)' : 'var(--bad)',
            border: `1px solid ${result.accepted ? 'var(--good)' : 'var(--bad)'}`,
          }}
        >
          {result.message}
        </div>
      )}
    </SettingsCard>
  );
}
